import React, { useEffect, useState } from 'react';
import { formatItemAmount, sendToProcore } from '../procoreActions';
import {
  asList,
  getJson,
  integrationError,
  integrationKind,
  integrationLabel,
  isErpIntegration,
  isProjectToolIntegration,
  isSyncableIntegration,
  sendKindFor,
} from '../helpers';

function billingTitle(billing) {
  return billing.billing_number || billing.vuid || 'Owner billing';
}

function billingGross(billing) {
  return Number(billing.total_amount || 0) + Number(billing.retention_held || 0);
}

function erpOwnerBillingNeedsSend(billing, mapping, integration) {
  if (!mapping) return true;
  if (integrationKind(integration) !== 'qbo') return false;
  const meta = mapping.external_metadata || {};
  const held = Number(billing.retention_held || 0);
  const released = Number(billing.retention_released || 0);
  if (held > 0 && !meta.retainage_qbo_journal_entry_id) return true;
  if (released > 0 && !meta.retention_release_qbo_journal_entry_id) return true;
  return false;
}

function billingSubtitle(billing) {
  const customer = billing.customer?.customer_name || billing.customer_name;
  return [customer, formatItemAmount(billingGross(billing))].filter(Boolean).join(' · ');
}

export default function OwnerBillingSyncModal({ projectVuid, onClose, onComplete }) {
  const [integrations, setIntegrations] = useState([]);
  const [billings, setBillings] = useState([]);
  const [procoreMappings, setProcoreMappings] = useState(new Set());
  const [erpMappings, setErpMappings] = useState({});
  const [integrationVuid, setIntegrationVuid] = useState('');
  const [direction, setDirection] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState('init');
  const [flash, setFlash] = useState(null);

  const integration = integrations.find((row) => row.vuid === integrationVuid) || integrations[0];
  const sendAllowed = isProjectToolIntegration(integration) || Boolean(sendKindFor(integration, 'project_billing'));
  const receiveAllowed = false;

  const resetChoice = () => {
    setDirection('');
    setItems([]);
    setSelected([]);
    setFlash(null);
  };

  useEffect(() => {
    let cancelled = false;
    setBusy('init');
    Promise.all([
      getJson('/api/integrations'),
      getJson('/api/project-billings', { project_vuid: projectVuid }),
      getJson('/api/external-system-ids', { object_type: 'project_billing' }).catch(() => []),
    ])
      .then(([integrationRows, billingRows, mappingRows]) => {
        if (cancelled) return;
        const nextIntegrations = asList(integrationRows).filter((row) => {
          if (String(row.status || '').toLowerCase() !== 'active') return false;
          return isSyncableIntegration(row);
        });
        setIntegrations(nextIntegrations);
        setIntegrationVuid(nextIntegrations[0]?.vuid || '');
        setBillings(asList(billingRows).filter((row) => !projectVuid || row.project_vuid === projectVuid));
        const nextProcore = new Set();
        const nextErp = {};
        asList(mappingRows).forEach((row) => {
          if (!row.object_vuid || !row.external_id) return;
          if (isProjectToolIntegration(row.integration || row)) nextProcore.add(row.object_vuid);
          if (isErpIntegration(row.integration || row)) nextErp[row.object_vuid] = row;
        });
        setProcoreMappings(nextProcore);
        setErpMappings(nextErp);
      })
      .catch((err) => {
        if (!cancelled) setFlash({ type: 'err', text: integrationError(err) });
      })
      .finally(() => {
        if (!cancelled) setBusy('');
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    resetChoice();
  }, [integrationVuid]);

  const loadItems = async (nextDirection) => {
    if (!integration) return;
    setBusy('load');
    setFlash(null);
    try {
      if (nextDirection !== 'send' || !sendAllowed) {
        setItems([]);
        setSelected([]);
        return;
      }
      const next = billings
        .filter((billing) =>
          isErpIntegration(integration)
            ? erpOwnerBillingNeedsSend(billing, erpMappings[billing.vuid], integration)
            : !procoreMappings.has(billing.vuid)
        )
        .map((billing) => ({
          id: billing.vuid,
          title: billingTitle(billing),
          subtitle: billingSubtitle(billing),
          sendKind: sendKindFor(integration, 'project_billing', 'project-billing'),
          payload: {
            project_billing_vuid: billing.vuid,
            ...(billing.contract?.vuid ? { contract_vuid: billing.contract.vuid } : {}),
          },
        }));
      setItems(next);
      setSelected(next.map((row) => row.id));
    } catch (err) {
      setItems([]);
      setSelected([]);
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const pickDirection = (nextDirection) => {
    setDirection(nextDirection);
    loadItems(nextDirection);
  };

  const toggleItem = (id) => {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const runSync = async () => {
    if (!integration || !direction) return;
    const chosen = items.filter((row) => selected.includes(row.id));
    if (!chosen.length) {
      setFlash({ type: 'err', text: 'Select at least one record.' });
      return;
    }
    setBusy('sync');
    setFlash(null);
    const errors = [];
    let sent = 0;
    try {
      if (direction === 'send') {
        for (const row of chosen) {
          try {
            const data = await sendToProcore(integration.vuid, row.sendKind, row.payload);
            sent += 1;
            if (data?.retainage_error) {
              errors.push(`${row.title}: Retainage journal entry: ${data.retainage_error}`);
            }
          } catch (err) {
            errors.push(`${row.title}: ${integrationError(err)}`);
          }
        }
      }
      const extra = errors.length ? `\n${errors.slice(0, 6).join('\n')}` : '';
      if (!sent && errors.length) {
        setFlash({ type: 'err', text: errors.slice(0, 6).join('\n') });
        return;
      }
      if (errors.length) {
        setFlash({ type: 'err', text: `Synced ${sent} item${sent === 1 ? '' : 's'}.${extra}` });
        return;
      }
      onComplete();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="pc-modal-backdrop" onClick={() => busy !== 'load' && busy !== 'sync' && onClose()}>
      <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>Sync owner billings</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pc-modal-body">
          <p className="pc-muted" style={{ marginTop: 0 }}>
            Choose an integration, then send this project’s owner billings to the project tool or ERP.
          </p>
          {busy === 'init' ? <div className="pc-empty">Loading integrations…</div> : null}
          {busy !== 'init' && integrations.length === 0 ? (
            <p className="pc-flash pc-flash-err">No active integrations found.</p>
          ) : null}
          {busy !== 'init' && integrations.length > 0 ? (
            <>
              <div className="pc-sync-options">
                {integrations.map((row) => (
                  <button
                    key={row.vuid}
                    type="button"
                    className={`pc-choice${integrationVuid === row.vuid ? ' is-selected' : ''}`}
                    onClick={() => {
                      setIntegrationVuid(row.vuid);
                      resetChoice();
                    }}
                  >
                    <strong>{row.integration_name}</strong>
                    <span>{integrationLabel(row)}</span>
                  </button>
                ))}
              </div>
              {integration ? (
                <div className="pc-sync-options">
                  <button
                    type="button"
                    className={`pc-choice${direction === 'send' ? ' is-selected' : ''}`}
                    disabled={!sendAllowed}
                    onClick={() => pickDirection('send')}
                  >
                    <strong>Send</strong>
                    <span>
                      {isErpIntegration(integration)
                        ? `Push this project’s owner billings to ${integrationLabel(integration)}.`
                        : isProjectToolIntegration(integration)
                          ? `Push this project’s owner billings to ${integrationLabel(integration)} as payment applications.`
                          : 'Send is only available for a project tool or ERP.'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`pc-choice${direction === 'receive' ? ' is-selected' : ''}`}
                    disabled={!receiveAllowed}
                    onClick={() => pickDirection('receive')}
                  >
                    <strong>Receive</strong>
                    <span>Owner billings are created in Claret and sent out. There is no retrieve.</span>
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          {busy === 'load' ? <div className="pc-empty">Loading unsynced records…</div> : null}
          {direction && busy !== 'load' && busy !== 'init' ? (
            items.length === 0 ? (
              <div className="pc-empty">No unsynced records for this integration.</div>
            ) : (
              <>
                <div className="pc-import-toolbar">
                  <button
                    type="button"
                    className="pc-btn pc-btn-secondary pc-btn-sm"
                    onClick={() => setSelected(items.map((row) => row.id))}
                  >
                    Select all
                  </button>
                  <button type="button" className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setSelected([])}>
                    Clear
                  </button>
                  <span className="pc-muted">{selected.length} selected</span>
                </div>
                <div className="pc-import-list">
                  {items.map((row) => (
                    <label key={row.id} className="pc-import-row">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleItem(row.id)}
                      />
                      <span>
                        <strong>{row.title}</strong>
                        <span className="pc-meta">{row.subtitle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )
          ) : null}
          {flash ? (
            <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
          ) : null}
        </div>
        <div className="pc-modal-actions">
          <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={busy === 'load' || busy === 'sync'}>
            Cancel
          </button>
          <button
            type="button"
            className="pc-btn"
            onClick={runSync}
            disabled={busy === 'load' || busy === 'sync' || busy === 'init' || !direction || !selected.length}
          >
            {busy === 'sync' ? 'Syncing…' : direction === 'receive' ? `Receive ${selected.length}` : `Send ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
